import { useRef, useState, useEffect, useCallback } from 'react';

// Draw or adopt a signature, producing a transparent PNG.
//
// Two ways in, because people expect both: draw it (trackpad, touch, stylus) or
// type a name and adopt a rendered script version. Both produce the same thing
// — an image — so everything downstream treats them identically. What is NOT
// identical is how they are recorded: signature_method distinguishes a person
// signing from an adopted signature, and that distinction is evidence.
//
// The image is produced here but stored by the edge function, because the
// signing page is unauthenticated and must not be able to write to a private
// bucket. The page hands over base64; the server decides where it lands.

const PAD_W = 600;
const PAD_H = 200;

/**
 * Crop a canvas to what was actually marked on it, plus a small margin.
 *
 * This is why typed initials came out microscopic. The pad is a fixed 600x200
 * box, so "A.B." was a small island of ink in a large, mostly empty image --
 * and the flattener fits the whole IMAGE to the field, not the ink inside it.
 * Two characters in a 600px-wide picture scaled into a 60pt box left the actual
 * letters a few points tall.
 *
 * Trimming means the image IS the mark, so it fills the field it was placed in.
 * It helps drawn signatures for the same reason -- people sign small, in the
 * middle of the box.
 */
function trimToInk(source) {
  const w = source.width, h = source.height;
  const ctx = source.getContext('2d');
  const { data } = ctx.getImageData(0, 0, w, h);
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // Alpha only: the ink is opaque, the background is transparent.
      if (data[(y * w + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;                       // nothing was drawn
  const pad = Math.round(Math.min(w, h) * 0.04);
  minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
  const out = document.createElement('canvas');
  out.width = maxX - minX + 1;
  out.height = maxY - minY + 1;
  out.getContext('2d').drawImage(source, minX, minY, out.width, out.height,
                                 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

// Handwriting styles offered for a typed signature.
//
// These are SYSTEM font stacks, not web fonts, and that is deliberate: this pad
// runs on the public signing page, which loads no third-party assets at all --
// the URL is the credential, and anything that phones home carries it. So the
// choices are fonts already on the signer's machine.
//
// Consequence worth knowing: the exact set available varies by device, so two
// signers may see slightly different renderings of the same choice. That does
// not matter here, because what gets stored is the PICTURE produced on the
// signer's own machine -- what they saw is what is on the document.
export const SIGNATURE_FONTS = [
  { id: 'roundhand', label: 'Flowing',  stack: '"Snell Roundhand", "Apple Chancery", "Segoe Script", cursive' },
  { id: 'brush',     label: 'Brush',    stack: '"Brush Script MT", "Bradley Hand", "Segoe Script", cursive' },
  { id: 'casual',    label: 'Casual',   stack: '"Bradley Hand", "Lucida Handwriting", "Segoe Script", cursive' },
  { id: 'formal',    label: 'Formal',   stack: 'Georgia, "Times New Roman", serif' },
];

// "Austin Bentson" -> "A.B." -- an initials box that types out a full name is
// not initials, and on a multi-page contract it is the thing appearing on every
// page. Only used as the starting value; anyone can type over it.
const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return parts.map((w) => w[0].toUpperCase()).join('.') + '.';
};

export default function SignaturePad({ mode = 'signature', defaultName = '', onDone, onCancel, C }) {
  const [tab, setTab] = useState('draw');       // draw | type
  const [font, setFont] = useState(SIGNATURE_FONTS[0]);
  const [typed, setTyped] = useState(mode === 'initials' ? initialsOf(defaultName) : defaultName);
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  const label = mode === 'initials' ? 'initials' : 'signature';

  // Size the canvas for the device so strokes are not blurry, while keeping the
  // CSS box fixed.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = PAD_W * dpr;
    c.height = PAD_H * dpr;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0d1b2a';
  }, [tab]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (PAD_W / r.width), y: (e.clientY - r.top) * (PAD_H / r.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = () => { drawing.current = false; last.current = null; };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  // Render the typed name to the same canvas size, so both paths hand back an
  // image with identical dimensions and the placed box behaves the same either way.
  const renderTyped = useCallback(() => {
    const c = document.createElement('canvas');
    const dpr = 3;                                  // crisp when scaled into a small field
    c.width = PAD_W * dpr; c.height = PAD_H * dpr;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#0d1b2a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const text = typed.trim();
    // Measure instead of guessing from length. The old length buckets made a
    // short string small for no reason and could still clip a wide one.
    let size = 120;
    do {
      ctx.font = `italic ${size}px ${font.stack}`;
      if (ctx.measureText(text).width <= PAD_W * 0.9 && size <= PAD_H * 0.7) break;
      size -= 2;
    } while (size > 12);
    ctx.fillText(text, PAD_W / 2, PAD_H / 2);
    return trimToInk(c);
  }, [typed, font]);

  const confirm = () => {
    if (tab === 'draw') {
      if (!hasInk) return;
      const dataUrl = trimToInk(canvasRef.current);
      if (!dataUrl) return;
      onDone?.({ dataUrl, method: 'drawn' });
    } else {
      if (!typed.trim()) return;
      const dataUrl = renderTyped();
      if (!dataUrl) return;
      onDone?.({ dataUrl, method: 'typed' });
    }
  };

  const ready = tab === 'draw' ? hasInk : !!typed.trim();
  const tabBtn = (id, text) => (
    <button onClick={() => setTab(id)}
            style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                     cursor: 'pointer',
                     border: `1px solid ${tab === id ? C.accent : C.border}`,
                     background: tab === id ? 'rgba(0,160,200,0.12)' : 'transparent',
                     color: tab === id ? C.accent : C.muted }}>{text}</button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 10000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px' }}
         onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px',
                    padding: '20px', width: '660px', maxWidth: '100%' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: C.bright, marginBottom: '3px',
                      textTransform: 'capitalize' }}>
          Add your {label}
        </div>
        <div style={{ fontSize: '11px', color: C.muted, marginBottom: '12px' }}>
          Draw it, or type your name and use a generated version. Either is a legally binding
          electronic signature.
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {tabBtn('draw', 'Draw')}
          {tabBtn('type', 'Type')}
        </div>

        {tab === 'draw' ? (
          <div style={{ position: 'relative' }}>
            <canvas ref={canvasRef}
                    onPointerDown={start} onPointerMove={move}
                    onPointerUp={end} onPointerLeave={end}
                    style={{ width: '100%', height: `${PAD_H}px`, background: '#fff',
                             borderRadius: '8px', border: `1px solid ${C.border}`,
                             touchAction: 'none', cursor: 'crosshair', display: 'block' }} />
            {!hasInk && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', pointerEvents: 'none',
                            color: '#98a6b3', fontSize: '13px' }}>
                Draw your {label} here
              </div>
            )}
            <div style={{ position: 'absolute', left: '5%', right: '5%', bottom: '34px',
                          borderBottom: '1px solid #cbd5e1', pointerEvents: 'none' }} />
          </div>
        ) : (
          <div>
            <input value={typed} onChange={(e) => setTyped(e.target.value)}
                   placeholder={mode === 'initials' ? 'Type your initials' : 'Type your full name'}
                   style={{ width: '100%', padding: '11px 13px', borderRadius: '8px',
                            border: `1px solid ${C.border}`, background: C.bg, color: C.bright,
                            fontSize: '15px', boxSizing: 'border-box', marginBottom: '10px' }} />
            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {SIGNATURE_FONTS.map((f) => (
                <button key={f.id} onClick={() => setFont(f)}
                        style={{ padding: '7px 12px', borderRadius: '6px', cursor: 'pointer',
                                 border: `1px solid ${font.id === f.id ? C.accent : C.border}`,
                                 background: font.id === f.id ? 'rgba(0,160,200,0.12)' : 'transparent',
                                 color: font.id === f.id ? C.accent : C.text,
                                 fontFamily: f.stack, fontStyle: 'italic', fontSize: '17px',
                                 lineHeight: 1.1 }}>
                  {(typed.trim() || 'Abc').slice(0, 12)}
                </button>
              ))}
            </div>
            <div style={{ height: `${PAD_H}px`, background: '#fff', borderRadius: '8px',
                          border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', overflow: 'hidden', padding: '0 14px' }}>
              <span style={{ color: '#0d1b2a', fontStyle: 'italic', whiteSpace: 'nowrap',
                             fontSize: typed.length > 22 ? '44px' : typed.length > 14 ? '60px' : '76px',
                             fontFamily: font.stack }}>
                {typed.trim() || (mode === 'initials' ? 'Your initials' : 'Your name')}
              </span>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '14px', alignItems: 'center' }}>
          <button onClick={confirm} disabled={!ready}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: 'none',
                           fontSize: '13px', fontWeight: 700,
                           cursor: ready ? 'pointer' : 'default',
                           background: ready ? C.accent : C.border,
                           color: ready ? '#04121d' : C.muted }}>
            Apply {label}
          </button>
          {tab === 'draw' && hasInk && (
            <button onClick={clear}
                    style={{ padding: '9px 14px', borderRadius: '8px', fontSize: '12px',
                             border: `1px solid ${C.border}`, background: 'transparent',
                             color: C.muted, cursor: 'pointer' }}>Clear</button>
          )}
          <button onClick={onCancel}
                  style={{ padding: '9px 14px', borderRadius: '8px', fontSize: '12px',
                           border: 'none', background: 'transparent', color: C.muted,
                           cursor: 'pointer', marginLeft: 'auto' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
